import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Trophy, Users, Medal, Flame, Award, Flag, CalendarCheck, Cog, Layers, Dumbbell, Crown, Check, Share2, Sparkles } from "lucide-react";
import api from "../api";
import { useUnit } from "../context/UnitContext";
import { ChallengeSection } from "../components/ChallengeSection";
import { PhotosSection } from "../components/PhotosSection";
import { BodyweightSection } from "../components/BodyweightSection";
import { InsightsSection } from "../components/InsightsSection";
import { DuelSection } from "../components/DuelSection";
import { PodiumSection } from "../components/PodiumSection";
import { MuscleSplit } from "../components/MuscleSplit";
import { shareRecordImage } from "../utils/shareRecord";
import { shareStreakImage } from "../utils/shareStreak";
import { WrappedModal } from "../components/WrappedModal";
import { useAuth } from "../context/AuthContext";

const medalColor = ["text-amber-400", "text-gray-300", "text-amber-700"];
const PR_LABELS = { poids: "Poids max", reps: "Reps max", volume: "Volume max" };
const BADGE_ICONS = {
  "premiere-seance": Flag, "regulier": CalendarCheck, "machine": Cog, "centurion": Layers,
  "infatigable": Flame, "briseur": Trophy, "colosse": Dumbbell, "record-du-mois": Crown,
};

export default function ProgressPage() {
  const { unit, dw, toLb } = useUnit();
  const { user } = useAuth();
  const [goals, setGoals] = useState({});
  const [editGoal, setEditGoal] = useState(null);
  const [goalVal, setGoalVal] = useState("");

  const loadGoals = () => api.get("/goals").then(({ data }) => setGoals(data)).catch(() => {});

  const saveGoal = async (exercice) => {
    if (!goalVal) return;
    await api.put("/goals", { exercice, target_poids: toLb(parseFloat(goalVal)) });
    setEditGoal(null);
    setGoalVal("");
    loadGoals();
  };

  const removeGoal = async (exercice) => {
    await api.delete(`/goals/${encodeURIComponent(exercice)}`);
    loadGoals();
  };

  const share = async (r) => {
    await shareRecordImage({
      exercice: r.exercice, muscle: r.muscle, poids: dw(r.poids_max), reps: r.reps_max,
      volume: dw(r.volume_max), date: r.poids_max_date || "", name: user.name, unit,
    });
  };
  const [records, setRecords] = useState([]);
  const [feed, setFeed] = useState([]);
  const [badges, setBadges] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState("");
  const [series, setSeries] = useState([]);
  const [metric, setMetric] = useState("poids_max");
  const [board, setBoard] = useState([]);
  const [boardEx, setBoardEx] = useState("");
  const [showWrapped, setShowWrapped] = useState(false);

  useEffect(() => {
    api.get("/records").then(({ data }) => {
      setRecords(data);
      if (data.length > 0) setSelected(data[0].exercice);
    });
    api.get("/stats").then(({ data }) => setStats(data));
    api.get("/feed").then(({ data }) => setFeed(data));
    api.get("/badges").then(({ data }) => setBadges(data));
    loadGoals();
    api.get("/leaderboard").then(({ data }) => {
      setBoard(data);
      const multi = data.find((b) => b.entries.length > 1);
      if (data.length > 0) setBoardEx((multi || data[0]).exercice);
    });
  }, []);

  const currentBoard = board.find((b) => b.exercice === boardEx);

  useEffect(() => {
    if (selected) api.get(`/progress/${encodeURIComponent(selected)}`).then(({ data }) => setSeries(data));
  }, [selected]);

  return (
    <div className="pb-28">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/85 border-b border-[#2E2E3A] px-4 pt-4 pb-3">
        <h1 className="font-display text-4xl text-white leading-none">PROGRESSION</h1>
        <p className="text-[#B0B0B4] text-xs mt-0.5">Records détectés automatiquement à chaque série</p>
      </header>
      <main className="px-4 mt-4 space-y-6">
        {stats && (
          <div className="grid grid-cols-4 gap-px bg-white/10 border border-[#2E2E3A]">
            {[["Séances", stats.nb_seances], ["Séries", stats.nb_series], [`Volume ${unit}`, Math.round(dw(stats.volume_total))], ["Records", stats.nb_prs]].map(([l, v]) => (
              <div key={l} className="bg-[#14141A] p-3 text-center">
                <p className="font-display text-2xl text-white">{v}</p>
                <p className="text-[#B0B0B4] text-[10px] uppercase tracking-wider">{l}</p>
              </div>
            ))}
          </div>
        )}

        {stats && (
          <div data-testid="streak-banner"
            className={`border px-4 py-3 flex items-center gap-3 ${
              stats.streak_weeks > 0 ? "bg-[#14141A] border-amber-400/30" : "bg-[#14141A] border-[#2E2E3A]"}`}>
            <Flame size={Math.min(22 + stats.streak_weeks * 4, 52)}
              className={`shrink-0 transition-all ${
                stats.streak_weeks >= 4 ? "text-[#FF3B30]" : stats.streak_weeks > 0 ? "text-amber-400" : "text-[#444]"}`} />
            <div className="flex-1">
              <p data-testid="streak-weeks" className="font-display text-2xl text-white leading-none">
                {stats.streak_weeks > 0
                  ? <>{stats.streak_weeks} SEMAINE{stats.streak_weeks > 1 ? "S" : ""} DE SUITE</>
                  : "PAS DE STREAK"}
              </p>
              <p className="text-[#B0B0B4] text-xs mt-1">
                {stats.streak_weeks >= 4 ? "La flamme est rouge — t'es une machine ! 🔥"
                  : stats.streak_weeks > 0 ? "Entraîne-toi chaque semaine pour faire grossir la flamme"
                  : "Fais une séance cette semaine pour allumer la flamme"}
              </p>
            </div>
            {stats.streak_weeks > 0 && (
              <button data-testid="share-streak-button"
                onClick={() => shareStreakImage({ streak: stats.streak_weeks, name: user.name })}
                className="text-[#B0B0B4] hover:text-[#FF3B30] p-2 transition-colors shrink-0" aria-label="Partager ma streak">
                <Share2 size={18} />
              </button>
            )}
          </div>
        )}

        <button data-testid="wrapped-open-button" onClick={() => setShowWrapped(true)}
          className="w-full bg-[#14141A] border border-[#FF3B30]/40 px-4 py-3 flex items-center gap-3 hover:bg-[#FF3B30]/5 transition-colors text-left">
          <Sparkles size={20} className="text-[#FF3B30] shrink-0" />
          <div>
            <p className="font-display text-xl text-white leading-none">RÉCAP {new Date().getFullYear()}</p>
            <p className="text-[#B0B0B4] text-xs mt-1">Ta rétrospective de l'année — volume, records, victoires</p>
          </div>
        </button>

        <ChallengeSection />

        <DuelSection />

        <PodiumSection />

        <MuscleSplit />

        <InsightsSection />

        {badges.length > 0 && (
          <section>
            <h2 className="font-display text-2xl text-white mb-2 flex items-center gap-2">
              BADGES <Award size={18} className="text-[#FF3B30]" />
            </h2>
            <div data-testid="badges-grid" className="grid grid-cols-4 gap-px bg-white/10 border border-[#2E2E3A]">
              {badges.map((b) => {
                const Icon = BADGE_ICONS[b.id] || Award;
                return (
                  <div key={b.id} data-testid={`badge-${b.id}`}
                    className={`bg-[#14141A] p-2.5 text-center ${b.earned ? "" : "opacity-35"}`}
                    title={b.desc}>
                    <Icon size={20} className={`mx-auto ${b.earned ? "text-amber-400" : "text-[#B0B0B4]"}`} />
                    <p className="text-white text-[10px] font-medium mt-1 leading-tight">{b.label}</p>
                    <p className="text-[#B0B0B4] text-[9px] mt-0.5 flex items-center justify-center gap-0.5">
                      {b.earned ? <Check size={10} className="text-[#32D74B]" /> : `${b.progress}/${b.target}`}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {feed.length > 0 && (
          <section>
            <h2 className="font-display text-2xl text-white mb-2 flex items-center gap-2">
              FIL D'ACTIVITÉ <Flame size={18} className="text-[#FF3B30]" />
            </h2>
            <div data-testid="activity-feed" className="bg-[#14141A] border border-[#2E2E3A] divide-y divide-white/5 max-h-64 overflow-y-auto">
              {feed.map((f) => (
                <div key={f.id} className={`px-4 py-2.5 ${f.is_me ? "bg-[#FF3B30]/5" : ""}`}>
                  <p className="text-sm text-white">
                    <span className="font-semibold">{f.is_me ? "Toi" : f.name}</span>
                    <span className="text-[#B0B0B4]"> a battu un record — </span>
                    {f.exercice}
                  </p>
                  <p className="text-xs text-[#B0B0B4] mt-0.5">
                    <span className="text-[#ccc] font-mono">{f.poids ? `${dw(f.poids)} ${unit}` : "PDC"} × {f.reps}</span>
                    <span className="text-amber-400/90"> · {f.prs.map((p) => PR_LABELS[p]).join(" · ")}</span>
                    <span> · {f.date}</span>
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {records.length === 0 ? (
          <p className="text-[#B0B0B4] text-sm text-center py-12">Aucune série enregistrée pour l'instant. Va t'entraîner !</p>
        ) : (
          <>
            <section>
              <h2 className="font-display text-2xl text-white mb-2">ÉVOLUTION</h2>
              <select data-testid="progress-exercise-select" value={selected} onChange={(e) => setSelected(e.target.value)}
                className="w-full h-11 bg-[#14141A] text-white text-sm px-3 border border-[#2E2E3A] focus:outline-none focus:ring-2 focus:ring-[#FF3B30]">
                {records.map((r) => <option key={r.exercice} value={r.exercice}>{r.exercice}</option>)}
              </select>
              <div className="flex gap-1.5 mt-2">
                {[["poids_max", "Poids max"], ["volume", "Volume"], ["reps_total", "Reps"]].map(([k, l]) => (
                  <button key={k} data-testid={`metric-${k}-button`} onClick={() => setMetric(k)}
                    className={`h-8 px-3 text-xs border transition-colors ${
                      metric === k ? "border-[#FF3B30] text-white bg-[#FF3B30]/10" : "border-[#2E2E3A] text-[#B0B0B4]"}`}>
                    {l}
                  </button>
                ))}
              </div>
              <div className="bg-[#14141A] border border-[#2E2E3A] mt-2 p-3 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series.map((d) => ({ ...d, poids_max: dw(d.poids_max), volume: Math.round(dw(d.volume)) }))} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                    <XAxis dataKey="date" stroke="#555" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                    <YAxis stroke="#555" fontSize={10} />
                    <Tooltip contentStyle={{ background: "#1C1C1E", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 12 }} />
                    <Line type="monotone" dataKey={metric} stroke="#FF3B30" strokeWidth={2.5} dot={{ fill: "#FF3B30", r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section>
              <h2 className="font-display text-2xl text-white mb-2 flex items-center gap-2">
                ENTRE AMIS <Users size={18} className="text-[#FF3B30]" />
              </h2>
              <select data-testid="leaderboard-exercise-select" value={boardEx} onChange={(e) => setBoardEx(e.target.value)}
                className="w-full h-11 bg-[#14141A] text-white text-sm px-3 border border-[#2E2E3A] focus:outline-none focus:ring-2 focus:ring-[#FF3B30]">
                {board.map((b) => <option key={b.exercice} value={b.exercice}>{b.exercice}</option>)}
              </select>
              <div className="bg-[#14141A] border border-[#2E2E3A] divide-y divide-white/5 mt-2">
                {currentBoard?.entries.map((e, i) => (
                  <div key={e.name + i} data-testid={`leaderboard-entry-${i + 1}`}
                    className={`px-4 py-3 flex items-center gap-3 ${e.is_me ? "bg-[#FF3B30]/5" : ""}`}>
                    <span className={`font-display text-2xl w-6 ${i < 3 ? medalColor[i] : "text-[#B0B0B4]"}`}>{i + 1}</span>
                    {i === 0 && <Medal size={16} className="text-amber-400 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{e.name}{e.is_me && <span className="text-[#FF3B30] text-xs"> · toi</span>}</p>
                      <p className="text-[#B0B0B4] text-[10px]">{e.nb_series} séries</p>
                    </div>
                    <div className="text-right shrink-0 text-xs text-[#ccc]">
                      <p><span className="text-white font-mono font-bold">{dw(e.poids_max)}</span> {unit} max</p>
                      <p className="text-[#B0B0B4]">{e.reps_max} reps · {Math.round(dw(e.volume_max))} {unit} vol.</p>
                    </div>
                  </div>
                ))}
                {currentBoard?.entries.length === 1 && (
                  <p className="px-4 py-2 text-[#B0B0B4] text-xs">Invite tes amis à s'inscrire pour comparer vos records !</p>
                )}
              </div>
            </section>

            <section>
              <h2 className="font-display text-2xl text-white mb-2 flex items-center gap-2">
                RECORDS PERSONNELS <Trophy size={18} className="text-amber-400" />
              </h2>
              <div className="bg-[#14141A] border border-[#2E2E3A] divide-y divide-white/5">
                {records.map((r) => (
                  <div key={r.exercice} data-testid={`record-row-${r.exercice}`} className="px-4 py-3">
                    <div className="flex items-center justify-between">
                      <p className="text-white text-sm font-medium">{r.exercice}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-[#B0B0B4] text-[10px]">{r.nb_series} séries</p>
                        <button data-testid={`share-record-${r.exercice}`} onClick={() => share(r)}
                          className="text-[#B0B0B4] hover:text-[#FF3B30] p-1 transition-colors" title="Partager ce record">
                          <Share2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-1 text-xs">
                      <span className="text-[#ccc]"><span className="text-[#FF3B30] font-mono font-bold">{dw(r.poids_max)}</span> {unit} max</span>
                      <span className="text-[#ccc]"><span className="text-[#FF3B30] font-mono font-bold">{r.reps_max}</span> reps max</span>
                      <span className="text-[#ccc]"><span className="text-[#FF3B30] font-mono font-bold">{Math.round(dw(r.volume_max))}</span> {unit} vol.</span>
                    </div>
                    {editGoal === r.exercice ? (
                      <div className="flex gap-1.5 mt-2 items-center">
                        <input data-testid={`goal-input-${r.exercice}`} type="number" inputMode="decimal" autoFocus
                          placeholder={`Objectif (${unit})`} className="set-input flex-1 !text-left !px-2 !h-9"
                          value={goalVal} onChange={(e) => setGoalVal(e.target.value)} />
                        <button data-testid={`goal-save-${r.exercice}`} onClick={() => saveGoal(r.exercice)}
                          className="h-9 px-3 bg-[#FF3B30] text-white text-xs font-semibold">OK</button>
                        <button onClick={() => setEditGoal(null)} className="h-9 px-2 text-[#B0B0B4] text-xs">Annuler</button>
                      </div>
                    ) : goals[r.exercice] ? (
                      <div className="mt-2" data-testid={`goal-bar-${r.exercice}`}>
                        <div className="h-1.5 bg-white/10 overflow-hidden">
                          <div className={`h-full transition-all ${r.poids_max >= goals[r.exercice] ? "bg-[#32D74B]" : "bg-[#FF3B30]"}`}
                            style={{ width: `${Math.min(100, (r.poids_max / goals[r.exercice]) * 100)}%` }} />
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-[10px] text-[#B0B0B4]">
                            Objectif : <span className="text-white font-mono">{dw(goals[r.exercice])} {unit}</span> · {Math.min(100, Math.round((r.poids_max / goals[r.exercice]) * 100))}%
                            {r.poids_max >= goals[r.exercice] && <span className="text-[#32D74B]"> · ATTEINT 🎉</span>}
                          </p>
                          <div className="flex gap-1">
                            <button data-testid={`goal-edit-${r.exercice}`} onClick={() => { setEditGoal(r.exercice); setGoalVal(String(dw(goals[r.exercice]))); }}
                              className="text-[9px] text-[#B0B0B4] hover:text-white uppercase">Modifier</button>
                            <button data-testid={`goal-delete-${r.exercice}`} onClick={() => removeGoal(r.exercice)}
                              className="text-[9px] text-[#555] hover:text-[#FF3B30] uppercase">Retirer</button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button data-testid={`goal-set-${r.exercice}`} onClick={() => { setEditGoal(r.exercice); setGoalVal(""); }}
                        className="mt-1.5 text-[10px] text-[#B0B0B4] hover:text-[#FF3B30] uppercase tracking-wider transition-colors">
                        🎯 Fixer un objectif
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        <BodyweightSection />

        <PhotosSection />
      </main>
      {showWrapped && <WrappedModal onClose={() => setShowWrapped(false)} />}
    </div>
  );
}
